/**
 * EspNowLiveMap
 *
 * Two modes:
 *   "live"  — shows router pins + coloured zone polygons + live schedule status
 *             (active zone overlays, elapsed/remaining timers, upcoming/past zones)
 *   "zones" — zone edit sidebar + leaflet-draw for drawing/replacing zone polygons,
 *             colour picker, zone name, notes
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polygon, Popup, Tooltip, useMap } from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import '../../styles/IrrigationLive.css';
import {
  RefreshCw, Pencil, Eye, Trash2, Check, X, MapPin, Clock, ChevronRight,
} from 'lucide-react';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
  type RelayZone,
  type LiveMapData,
  type GeoJSONPolygon,
  getRelayState,
  isPlaceholderZoneName,
  relayCountFor,
  relayDisplayName,
  formatDuration,
} from '../../services/espnowIrrigation';
import sensorDevicesService, { type SensorDevice, type SensorChannel } from '../../services/sensorDevices';
import orchardService from '../../services/orchard';
import { utcSecToLocalHHMM, useOrgTimezoneOrNull } from '../../utils/timezone';
import { siteOffsetSec } from '../../utils/irrigationClock';
import { localDaySec, formatRunTime } from '../../utils/irrigationRuns';
import { buildLiveMapStatus, type ValveStatus } from '../../utils/liveMapStatus';
import LiveMapNowNext from './LiveMapNowNext';
import RainSkipNotice from './RainSkipNotice';
import { TILE_LAYERS, MAP_MAX_ZOOM, type TileLayerKey } from '../../utils/leafletGeo';
import ZoneDrawControl from '../map/ZoneDrawControl';
import { geoJsonRingToLatLng } from '../../utils/geoLeaflet';
import LayerToggleControl from '../map/LayerToggleControl';

// ─── Leaflet icon fix ─────────────────────────────────────────────────────────

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { nodeState, nodeStateLabel, nodeStateDot } from '../../utils/nodeState';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

// ─── Zone colour palette ──────────────────────────────────────────────────────

const ZONE_COLOURS = [
  { hex: '#3B82F6', label: 'Blue' },
  { hex: '#22C55E', label: 'Green' },
  { hex: '#EAB308', label: 'Yellow' },
  { hex: '#F97316', label: 'Orange' },
  { hex: '#EF4444', label: 'Red' },
  { hex: '#A855F7', label: 'Purple' },
  { hex: '#14B8A6', label: 'Teal' },
  { hex: '#EC4899', label: 'Pink' },
];

// ─── GeoJSON ↔ Leaflet coordinate helpers ─────────────────────────────────────

// ─── Types ────────────────────────────────────────────────────────────────────

interface RelayKey { routerMac: string; relayIndex: number }

// ─── Props ────────────────────────────────────────────────────────────────────

interface EspNowLiveMapProps {
  controller: EspNowController;
  routers: EspNowRouter[];
  orgTimezone: string;
  onPlaceRouter: (routerMac: string, lat: number, lng: number) => void;
  onRoutersChange?: (routers: EspNowRouter[]) => void;
  height?: number;
}

/** Sensor icon using Leaflet divIcon */
function createSensorIcon(online: boolean) {
  return L.divIcon({
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:${online ? '#7c3aed' : '#9ca3af'};
      border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);
      display:flex;align-items:center;justify-content:center;
      color:white;font-size:14px;
    ">&#x25C9;</div>`,
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EspNowLiveMap({
  controller,
  routers,
  orgTimezone,
  onPlaceRouter,
  height = 540,
}: EspNowLiveMapProps) {
  const [mode, setMode] = useState<'live' | 'zones'>('live');
  const [liveData, setLiveData] = useState<LiveMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number>(Date.now());
  const [tick, setTick] = useState(0);

  // Zone editing state
  const [selectedRelay, setSelectedRelay] = useState<RelayKey | null>(null);
  const [editZone, setEditZone] = useState<Partial<RelayZone>>({});
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);

  // Sensor state
  const [sensorDevice, setSensorDevice] = useState<(SensorDevice & { channels?: SensorChannel[]; latestReadings?: Array<{ channel_index: number; channel_kind: string; value: number | null; reading_time: string }> }) | null>(null);

  // Farm boundary + centre — the map zooms to the farm the controller sits on
  const [farmCenter, setFarmCenter] = useState<[number, number] | null>(null);
  const [farmRing, setFarmRing] = useState<[number, number][] | null>(null);
  const [mapLayer, setMapLayer] = useState<TileLayerKey>('satellite');

  const orgTzOrNull = useOrgTimezoneOrNull();

  const mapCenterRef = useRef<[number, number] | null>(null);

  // Clicking a run in the panel above the map should take you to it. Held as a
  // token so clicking the same zone twice still flies there — a plain key would
  // be unchanged and the effect would not run.
  const [focus, setFocus] = useState<{ key: string; token: number } | null>(null);

  const fetchLiveData = useCallback(async () => {
    try {
      const data = await espnowService.getLiveMap(controller.controller_id);
      setLiveData(data);
      setFetchedAt(Date.now());
      setError(null);
    } catch {
      setError('Failed to load map data');
    } finally {
      setLoading(false);
    }
  }, [controller.controller_id]);

  // Fetch the farm boundary — what the map fits to on open
  useEffect(() => {
    if (!controller.farm_id) return;
    orchardService.getFarm(controller.farm_id)
      .then(res => {
        if (!res.success) return;
        const farm = res.data;
        const ring = farm?.boundary_geometry?.coordinates?.[0];
        const latlngs = ring && ring.length >= 3 ? geoJsonRingToLatLng(ring) : null;
        setFarmRing(latlngs);
        if (farm?.center_point) {
          setFarmCenter([farm.center_point.lat, farm.center_point.lng]);
        } else if (latlngs) {
          const c = L.latLngBounds(latlngs).getCenter();
          setFarmCenter([c.lat, c.lng]);
        }
      })
      .catch(() => {});
  }, [controller.farm_id]);

  // Fetch linked sensor device + channels for map markers
  const fetchSensorData = useCallback(async () => {
    try {
      const data = await espnowService.getSensorForController(controller.controller_id);
      setSensorDevice(data);
    } catch { /* no sensor set up — fine */ }
  }, [controller.controller_id]);

  // Initial load + auto-refresh every 30s
  useEffect(() => {
    fetchLiveData();
    fetchSensorData();
    const iv = setInterval(() => { fetchLiveData(); fetchSensorData(); }, 30_000);
    return () => clearInterval(iv);
  }, [fetchLiveData, fetchSensorData]);

  // 1-second ticker for live timers
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  // Current seconds-from-midnight, derived from server now_sec + elapsed client
  // time, then folded into the LOCAL day.
  //
  // now_sec and every start_sec are UTC, but the timeline holds the programs
  // that today's LOCAL weekday selected. Compared raw, a +10 site at 7 AM has
  // nowSec 75600 against today's runs at 0/3600/14400 - every one of them
  // numerically behind it and every one still to come, so the whole day reads
  // as past. See localDaySec. The fold also wraps the running total, which
  // could otherwise pass 86400 on a page left open over midnight.
  // NOT the viewer's browser timezone. `useOrgTimezone()` silently falls back
  // to it, and two people looking at one farm then folded by whatever their
  // machines were set to - same bundle, same payload, different answers about
  // what was watering. See siteOffsetSec.
  const offsetSec = siteOffsetSec(orgTzOrNull, controller.timezone_offset);
  const nowSec = liveData
    ? localDaySec(liveData.now_sec + Math.floor((Date.now() - fetchedAt) / 1000), offsetSec)
    : 0;

  // Zone lookup
  const zoneMap = new Map<string, RelayZone>();
  if (liveData) {
    for (const z of liveData.zones) {
      zoneMap.set(`${z.router_mac}:${z.relay_index}`, z);
    }
  }

  // What every valve is doing right now — timed from its OWN duration rather
  // than its step's, so a two-minute run inside a twenty-minute step is not
  // drawn as watering for twenty. One derivation feeds the panel above the
  // map, the polygon styling and the popups, so the three cannot disagree.
  const valveStatus = liveData
    ? buildLiveMapStatus({
        liveData,
        routers,
        transport: controller.transport,
        nowSec,
        offsetSec,
        nowMs: Date.now(),
      })
    : null;


  // When a relay is selected for editing, populate editZone from existing zone or defaults
  /** The node's own label for a relay, if one has been typed. */
  function relayLabelOf(key: RelayKey): string {
    const router = (liveData?.routers ?? routers).find(r => r.router_mac === key.routerMac);
    return (router?.relay_labels?.[String(key.relayIndex)] ?? '').trim();
  }

  // A zone left unnamed stays unnamed — the field shows the relay's own label
  // as its placeholder instead. Prefilling it with "Relay 3" saved that string
  // as if somebody had chosen it, and it then outranked every label typed
  // afterwards. An existing zone carrying one of those is offered blank, so
  // saving clears it.
  function selectRelay(key: RelayKey) {
    setSelectedRelay(key);
    setDrawing(false);
    const existing = zoneMap.get(`${key.routerMac}:${key.relayIndex}`);
    if (!existing) {
      setEditZone({ zone_color: '#3B82F6' });
      return;
    }
    setEditZone({
      ...existing,
      zone_name: isPlaceholderZoneName(existing.zone_name) ? '' : existing.zone_name,
    });
  }

  async function saveZone(geometry?: GeoJSONPolygon | null) {
    if (!selectedRelay) return;
    setSaving(true);
    try {
      const saved = await espnowService.updateRelayZone(
        selectedRelay.routerMac,
        selectedRelay.relayIndex,
        controller.controller_id,
        {
          zone_name: editZone.zone_name || null,
          zone_color: editZone.zone_color || '#3B82F6',
          boundary_geometry: geometry !== undefined ? geometry : editZone.boundary_geometry ?? null,
          notes: editZone.notes || null,
        }
      );
      // Merge into liveData zones
      setLiveData(prev => {
        if (!prev) return prev;
        const key = `${saved.router_mac}:${saved.relay_index}`;
        const filtered = prev.zones.filter(z => `${z.router_mac}:${z.relay_index}` !== key);
        return { ...prev, zones: [...filtered, saved] };
      });
      zoneMap.set(`${saved.router_mac}:${saved.relay_index}`, saved);
      setEditZone({ ...saved });
      setDrawing(false);
    } catch {
      setError('Failed to save zone');
    } finally {
      setSaving(false);
    }
  }

  async function deleteZone() {
    if (!selectedRelay) return;
    if (!confirm('Delete zone polygon for this relay?')) return;
    setSaving(true);
    try {
      await espnowService.deleteRelayZone(
        selectedRelay.routerMac,
        selectedRelay.relayIndex,
        controller.controller_id
      );
      setLiveData(prev => {
        if (!prev) return prev;
        const key = `${selectedRelay.routerMac}:${selectedRelay.relayIndex}`;
        return { ...prev, zones: prev.zones.filter(z => `${z.router_mac}:${z.relay_index}` !== key) };
      });
      setEditZone(prev => ({ ...prev, boundary_geometry: null, id: undefined } as Partial<RelayZone>));
      setDrawing(false);
    } catch {
      setError('Failed to delete zone');
    } finally {
      setSaving(false);
    }
  }

  // Flat list of all relays across all routers for the sidebar
  const allRelays: Array<RelayKey & { routerName: string; label: string }> = [];
  for (const r of (liveData?.routers ?? routers)) {
    // Only what the board has. Eight here listed four valves per XBee node that
    // are not on the hardware, and each one was selectable and mappable.
    for (let i = 0; i < relayCountFor(r, controller.transport); i++) {
      allRelays.push({
        routerMac: r.router_mac,
        relayIndex: i,
        routerName: r.name || r.router_mac.slice(-6),
        label: r.relay_labels?.[String(i)] || `Relay ${i}`,
      });
    }
  }

  const placedRouters = (liveData?.routers ?? routers).filter(r => r.lat !== null && r.lng !== null);
  const sensorHasLocation = sensorDevice?.lat != null && sensorDevice?.lng != null;
  const defaultCenter: [number, number] = farmCenter
    ?? (placedRouters.length > 0
      ? [placedRouters[0].lat!, placedRouters[0].lng!]
      : sensorHasLocation
        ? [sensorDevice!.lat!, sensorDevice!.lng!]
        : [-33.8688, 151.2093]);

  /**
   * Everything the map should have in view: the farm boundary first, plus any
   * placed routers, the sensor and drawn zones. The union can only widen the
   * farm's own bounds, so a device sitting just outside the boundary still
   * lands on screen.
   */
  const fitPoints: [number, number][] = [];
  if (farmRing) fitPoints.push(...farmRing);
  for (const r of placedRouters) fitPoints.push([r.lat!, r.lng!]);
  if (sensorHasLocation) fitPoints.push([sensorDevice!.lat!, sensorDevice!.lng!]);
  for (const z of liveData?.zones ?? []) {
    const ring = z.boundary_geometry?.coordinates?.[0];
    if (ring && ring.length >= 3) fitPoints.push(...geoJsonRingToLatLng(ring));
  }
  // Only re-fit when the set of things to show actually changes, not on every
  // 30s poll — otherwise the map yanks itself back while someone is panning.
  const fitKey = `${farmRing ? 'farm' : 'nofarm'}:${fitPoints.length}`;

  // Where a click in the panel should take the map. A mapped zone flies to its
  // own boundary; one nobody has drawn falls back to its node, which is still
  // more use than not moving at all.
  const focusPoints: [number, number][] | null = (() => {
    if (!focus) return null;
    const ring = zoneMap.get(focus.key)?.boundary_geometry?.coordinates?.[0];
    if (ring && ring.length >= 3) return geoJsonRingToLatLng(ring);
    const mac = focus.key.split(':')[0];
    const node = (liveData?.routers ?? routers).find(r => r.router_mac === mac);
    return node?.lat != null && node?.lng != null ? [[node.lat, node.lng]] : null;
  })();

  void tick; // reference tick to re-render timers

  return (
    <div className="space-y-3">
      {/* Mode toggle + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => { setMode('live'); setSelectedRelay(null); setDrawing(false); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === 'live' ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Eye className="w-4 h-4" /> Live View
          </button>
          <button
            onClick={() => setMode('zones')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === 'zones' ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Pencil className="w-4 h-4" /> Edit Zones
          </button>
        </div>
        <button
          onClick={fetchLiveData}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg bg-white transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400">×</button>
        </div>
      )}

      {/* Place sensor prompt when sensor exists but has no location */}
      {sensorDevice && !sensorHasLocation && (
        <div className="flex items-center gap-2 p-2 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-700">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          Sensor device has no location. Click to place it at the map center.
          <button
            className="ml-auto px-2 py-1 bg-violet-600 text-white rounded text-xs hover:bg-violet-700"
            onClick={async () => {
              const center = defaultCenter;
              try {
                await sensorDevicesService.updateDevice(sensorDevice.device_id, { lat: center[0], lng: center[1] });
                setSensorDevice(prev => prev ? { ...prev, lat: center[0], lng: center[1] } : prev);
              } catch { setError('Failed to place sensor'); }
            }}
          >
            Place Sensor
          </button>
        </div>
      )}

      {/* ── What rain left dry today ── */}
      {mode === 'live' && (liveData?.rain_skips ?? []).length > 0 && (
        <RainSkipNotice
          skips={liveData?.rain_skips ?? []}
          orgTimezone={orgTimezone}
          nameOf={(mac, relay) => {
            const zone = zoneMap.get(`${mac}:${relay}`);
            const label = (zone?.zone_name ?? '').trim()
              || relayLabelOf({ routerMac: mac, relayIndex: relay });
            return label || `${mac.slice(-8)} / Relay ${relay}`;
          }}
        />
      )}

      {/* ── What is on now, and what is next ── */}
      {mode === 'live' && valveStatus && (
        <LiveMapNowNext
          status={valveStatus}
          orgTimezone={orgTimezone}
          onSelectValve={valve => setFocus({ key: valve.key, token: Date.now() })}
        />
      )}

      <div className="flex flex-col md:flex-row gap-3">
        {/* ── Zone edit sidebar (zones mode only) ── */}
        {mode === 'zones' && (
          <div className="md:w-64 flex-shrink-0 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Relays</p>
            <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
              {allRelays.map(item => {
                const key = `${item.routerMac}:${item.relayIndex}`;
                const zone = zoneMap.get(key);
                const isSel = selectedRelay?.routerMac === item.routerMac && selectedRelay?.relayIndex === item.relayIndex;
                return (
                  <button
                    key={key}
                    onClick={() => selectRelay({ routerMac: item.routerMac, relayIndex: item.relayIndex })}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      isSel
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0 border border-white shadow"
                      style={{ background: zone?.zone_color ?? '#D1D5DB' }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-gray-800 truncate">{item.label}</div>
                      <div className="text-xs text-gray-400 truncate">{item.routerName}</div>
                    </div>
                    {zone?.boundary_geometry && (
                      <MapPin className="w-3 h-3 text-green-500 flex-shrink-0" />
                    )}
                    <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>

            {/* Zone detail panel */}
            {selectedRelay && (
              <div className="border border-gray-200 rounded-lg bg-white p-3 space-y-3 mt-2">
                <p className="text-xs font-semibold text-gray-600">Zone Config</p>

                <div>
                  <label className="text-xs text-gray-500 block mb-1">Zone Name</label>
                  <input
                    type="text"
                    maxLength={64}
                    value={editZone.zone_name ?? ''}
                    onChange={e => setEditZone(z => ({ ...z, zone_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={relayLabelOf(selectedRelay) || 'e.g. North Block Row 1'}
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 block mb-1">Colour</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ZONE_COLOURS.map(c => (
                      <button
                        key={c.hex}
                        title={c.label}
                        onClick={() => setEditZone(z => ({ ...z, zone_color: c.hex }))}
                        className={`w-6 h-6 rounded-full border-2 transition-transform ${
                          editZone.zone_color === c.hex ? 'border-gray-800 scale-110' : 'border-transparent'
                        }`}
                        style={{ background: c.hex }}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 block mb-1">Notes</label>
                  <textarea
                    rows={2}
                    maxLength={500}
                    value={editZone.notes ?? ''}
                    onChange={e => setEditZone(z => ({ ...z, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    placeholder="Optional notes"
                  />
                </div>

                {/* Polygon actions */}
                <div className="space-y-1.5">
                  {editZone.boundary_geometry ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-green-700 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Zone drawn
                      </span>
                      <button
                        onClick={() => setDrawing(true)}
                        className="text-xs text-blue-600 hover:underline ml-auto"
                      >
                        Redraw
                      </button>
                      <button onClick={deleteZone} disabled={saving} className="text-xs text-red-500 hover:underline">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDrawing(true)}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                    >
                      <Pencil className="w-3 h-3" /> Draw Zone on Map
                    </button>
                  )}
                  {drawing && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">
                      Click on the map to draw a polygon. Click the first point to close it, or use the Finish button.
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => saveZone()}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" /> {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setSelectedRelay(null); setDrawing(false); }}
                    className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Map ── */}
        {/* flex-1 only once the row is side by side. Below md the parent is a
            column, where flex-1's zero basis overrides `height` and the map
            collapses to its border — blank on every phone. */}
        <div className="w-full md:flex-1 md:min-w-0 rounded-xl overflow-hidden border border-gray-200" style={{ height }}>
          {loading && !liveData ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading map data…
            </div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={15}
              maxZoom={MAP_MAX_ZOOM}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution={TILE_LAYERS[mapLayer].attribution}
                url={TILE_LAYERS[mapLayer].url}
                maxNativeZoom={TILE_LAYERS[mapLayer].maxNativeZoom}
                maxZoom={TILE_LAYERS[mapLayer].maxZoom}
              />
              <LayerToggleControl
                mapLayer={mapLayer}
                onToggle={() => setMapLayer((l) => (l === 'street' ? 'satellite' : 'street'))}
              />

              <FitToContent points={fitPoints} fitKey={fitKey} fallbackCenter={farmCenter} />
              <FocusZone points={focusPoints} token={focus?.token ?? 0} />
              <MapCenterTracker centerRef={mapCenterRef} />

              {/* Zone polygons */}
              {liveData && liveData.zones.map(zone => {
                if (!zone.boundary_geometry) return null;
                const coords = zone.boundary_geometry.coordinates[0];
                if (!coords || coords.length < 3) return null;
                const latlngs = geoJsonRingToLatLng(coords);

                const relayKey = `${zone.router_mac}:${zone.relay_index}`;
                const valve = valveStatus?.byKey.get(relayKey) ?? null;
                const state = valve?.state;
                // Open, or inside a window nobody has contradicted. A valve the
                // node has reported shut inside its window is deliberately NOT
                // drawn as watering — that block is dry, and the panel says so.
                const wet = state === 'running' || state === 'manual' || state === 'due';

                let fillOpacity = 0.08;
                let weight = 1;
                let color = zone.zone_color;

                if (wet) {
                  fillOpacity = state === 'due' ? 0.25 : 0.45;
                  weight = 3;
                } else if (state === 'upcoming') {
                  fillOpacity = 0.18;
                  weight = 2;
                }

                const isSelected = selectedRelay?.routerMac === zone.router_mac
                  && selectedRelay?.relayIndex === zone.relay_index;

                if (isSelected) {
                  fillOpacity = Math.max(fillOpacity, 0.3);
                  weight = 3;
                  color = editZone.zone_color || zone.zone_color;
                }

                // A dashed outline means "not water on the ground": a sequence
                // that was skipped, a program that did not fire, or a window
                // running with no valve report to confirm it. The flowing dash
                // is reserved for a valve the node has actually reported open —
                // same idiom as the irrigation landing map.
                const dashed = wet || state === 'skipped' || state === 'did-not-start';
                const confirmed = state === 'running' || state === 'manual';

                return (
                  <Polygon
                    key={relayKey}
                    positions={latlngs}
                    pathOptions={{
                      color,
                      fillColor: color,
                      fillOpacity,
                      weight,
                      dashArray: dashed ? '8 6' : undefined,
                      className: mode === 'live' && confirmed ? 'zone-running' : undefined,
                    }}
                  >
                    {/* The name and the countdown ON the map. Reading either
                        used to need a click, which is no use when the question
                        is "which block is on?". Live mode only — the zone
                        editor needs the polygons unobstructed. */}
                    {mode === 'live' && valve && (wet || state === 'did-not-start') && (
                      <Tooltip permanent direction="center" className="zone-run-label">
                        {valve.zoneName}
                        {valve.remainingSec != null && ` · ${formatRunTime(valve.remainingSec)} left`}
                        {state === 'manual' && ' · manual run'}
                        {state === 'due' && ' · due'}
                        {state === 'did-not-start' && ' · did not start'}
                      </Tooltip>
                    )}
                    <Popup>
                      <ZonePopup
                        zone={zone}
                        name={valve?.zoneName ?? relayDisplayName(
                          zone.zone_name,
                          (liveData?.routers ?? routers).find(r => r.router_mac === zone.router_mac)?.relay_labels,
                          zone.relay_index
                        )}
                        valve={valve}
                      />
                    </Popup>
                  </Polygon>
                );
              })}

              {/* Router markers */}
              {placedRouters.map(router => (
                <DraggableRouterMarker
                  key={router.router_mac}
                  router={router}
                  onDragEnd={onPlaceRouter}
                  zoneMap={zoneMap}
                  transport={controller.transport}
                />
              ))}

              {/* Sensor device marker */}
              {sensorDevice && sensorHasLocation && (
                <SensorDeviceMarker
                  device={sensorDevice}
                  onDragEnd={async (lat, lng) => {
                    try {
                      await sensorDevicesService.updateDevice(sensorDevice.device_id, { lat, lng });
                      setSensorDevice(prev => prev ? { ...prev, lat, lng } : prev);
                    } catch { setError('Failed to save sensor location'); }
                  }}
                />
              )}

              {/* Leaflet-draw control (zones mode + relay selected + drawing) */}
              {mode === 'zones' && selectedRelay && drawing && (
                <ZoneDrawControl
                  color={editZone.zone_color || '#3B82F6'}
                  existingGeometry={editZone.boundary_geometry ?? null}
                  onZoneDrawn={geometry => {
                    setEditZone(z => ({ ...z, boundary_geometry: geometry }));
                    saveZone(geometry);
                    setDrawing(false);
                  }}
                  onCancel={() => setDrawing(false)}
                />
              )}
              <MapTouchGate disabled={drawing} />
            </MapContainer>
          )}
        </div>
      </div>

      {/* ── Today's schedule timeline (live mode) ── */}
      {mode === 'live' && liveData && liveData.timeline.length > 0 && (
        <ScheduleTimeline timeline={liveData.timeline} nowSec={nowSec} offsetSec={offsetSec} zoneMap={zoneMap} routers={liveData.routers} orgTimezone={orgTimezone} />
      )}
    </div>
  );
}

// ─── Zone popup content ───────────────────────────────────────────────────────

function ZonePopup({ zone, name, valve }: { zone: RelayZone; name: string; valve: ValveStatus | null }) {
  return (
    <div className="text-sm min-w-[160px]">
      <div className="font-semibold mb-1" style={{ color: zone.zone_color }}>
        {name}
      </div>
      {valve?.state === 'running' && (
        <div className="text-green-700 text-xs font-medium flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
          Watering — {formatRunTime(valve.remainingSec ?? 0)} left of{' '}
          {formatRunTime(valve.durationSec ?? 0)}
        </div>
      )}
      {valve?.state === 'manual' && (
        <div className="text-amber-700 text-xs font-medium flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
          On, with no timed program — nothing will close it
        </div>
      )}
      {valve?.state === 'due' && (
        <div className="text-gray-600 text-xs">
          Should be watering — {formatRunTime(valve.remainingSec ?? 0)} left, but no valve report
          since it was due
        </div>
      )}
      {valve?.state === 'did-not-start' && (
        <div className="text-red-700 text-xs">
          Did not start — its program has been running {formatRunTime(valve.elapsedSec ?? 0)} and
          the node reports this valve closed
        </div>
      )}
      {valve?.state === 'skipped' && (
        <div className="text-red-700 text-xs">
          Skipped {formatRunTime(valve.skippedAgoSec ?? 0)} ago — a node did not answer
        </div>
      )}
      {valve?.state === 'upcoming' && (
        <div className="text-blue-700 text-xs">
          Starts in {formatRunTime(valve.startsInSec ?? 0)} — for{' '}
          {formatRunTime(valve.durationSec ?? 0)}
        </div>
      )}
      {valve?.state === 'finished' && (
        <div className="text-gray-400 text-xs">
          Finished {formatRunTime(valve.endedAgoSec ?? 0)} ago
        </div>
      )}
      {(!valve || valve.state === 'idle') && (
        <div className="text-gray-400 text-xs">Not scheduled today</div>
      )}
      {zone.notes && <div className="text-xs text-gray-500 mt-1 border-t border-gray-100 pt-1">{zone.notes}</div>}
    </div>
  );
}

// ─── Draggable router marker ──────────────────────────────────────────────────

function DraggableRouterMarker({
  router,
  onDragEnd,
  zoneMap,
  transport,
}: {
  router: EspNowRouter;
  onDragEnd: (mac: string, lat: number, lng: number) => void;
  zoneMap: Map<string, RelayZone>;
  transport: string | null;
}) {
  const relayBits = Array.from(
    { length: relayCountFor(router, transport) },
    (_, i) => getRelayState(router.relay_states, i),
  );
  const activeRelays = relayBits.filter(Boolean).length;

  const icon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
    className: router.online ? 'hue-rotate-[140deg]' : 'grayscale opacity-60',
  });

  return (
    <Marker
      position={[router.lat!, router.lng!]}
      icon={icon}
      draggable
      eventHandlers={{
        dragend: e => {
          const ll = (e.target as L.Marker).getLatLng();
          onDragEnd(router.router_mac, ll.lat, ll.lng);
        },
      }}
    >
      <Popup>
        <div className="text-sm min-w-[180px]">
          <div className="font-semibold mb-1">{router.name || router.router_mac}</div>
          <div className="flex items-center gap-1 mb-2 text-xs text-gray-500">
            <span className={`w-2 h-2 rounded-full ${nodeStateDot(nodeState(router))}`} />
            {nodeStateLabel(nodeState(router))}
            {router.rssi_dbm !== null && ` · ${router.rssi_dbm} dBm`}
          </div>
          <div className="grid grid-cols-4 gap-0.5">
            {relayBits.map((on, i) => {
              const zone = zoneMap.get(`${router.router_mac}:${i}`);
              const label = relayDisplayName(zone?.zone_name, router.relay_labels, i, `${i}`);
              return (
                <div
                  key={i}
                  title={label}
                  className={`text-center text-xs rounded px-1 py-0.5 ${on ? 'text-white' : 'bg-gray-100 text-gray-400'}`}
                  style={on ? { background: zone?.zone_color ?? '#3B82F6' } : undefined}
                >
                  {label.slice(0, 4)}
                </div>
              );
            })}
          </div>
          {activeRelays > 0 && (
            <div className="text-xs text-blue-700 font-medium mt-1">{activeRelays} relay{activeRelays !== 1 ? 's' : ''} active</div>
          )}
          <div className="text-xs text-gray-400 mt-1">Drag to reposition</div>
        </div>
      </Popup>
    </Marker>
  );
}

// ─── FitBounds helper ─────────────────────────────────────────────────────────

/**
 * Fit to the farm (plus its devices and zones) once that data has loaded.
 * The farm boundary arrives after the map has mounted, so MapContainer's
 * `center` prop is already stale by then — the view has to be set imperatively.
 */
function FitToContent({
  points,
  fitKey,
  fallbackCenter,
}: {
  points: [number, number][];
  fitKey: string;
  fallbackCenter: [number, number] | null;
}) {
  const map = useMap();
  const lastFit = useRef<string | null>(null);

  useEffect(() => {
    if (lastFit.current === fitKey) return;
    // No animation: a zoom still in flight when the page unmounts throws from Leaflet.
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 17, animate: false });
      lastFit.current = fitKey;
    } else if (fallbackCenter) {
      map.setView(fallbackCenter, 16, { animate: false });
      lastFit.current = fitKey;
    }
    // `points` is rebuilt every render; fitKey is what actually gates the fit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey, fallbackCenter]);

  return null;
}

/**
 * Fly to one zone when somebody picks it out of the panel above the map.
 *
 * Gated on a token rather than on the points themselves: `points` is rebuilt
 * every render (the map re-renders every second for the countdowns), so an
 * effect keyed on it would fight whoever is panning.
 */
function FocusZone({ points, token }: { points: [number, number][] | null; token: number }) {
  const map = useMap();
  const lastToken = useRef(0);

  useEffect(() => {
    if (!token || token === lastToken.current) return;
    if (!points || points.length === 0) return;
    lastToken.current = token;
    map.flyToBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 18 });
    // `points` is rebuilt every render; the token is what gates the fly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, token]);

  return null;
}

function MapCenterTracker({ centerRef }: { centerRef: React.MutableRefObject<[number, number] | null> }) {
  const map = useMap();
  useEffect(() => {
    const update = () => { const c = map.getCenter(); centerRef.current = [c.lat, c.lng]; };
    update();
    map.on('move', update);
    return () => { map.off('move', update); };
  }, [map, centerRef]);
  return null;
}

// ─── Schedule timeline strip ──────────────────────────────────────────────────

function ScheduleTimeline({
  timeline,
  nowSec,
  offsetSec,
  zoneMap,
  routers,
  orgTimezone,
}: {
  timeline: LiveMapData['timeline'];
  nowSec: number;
  /** The SITE's offset, passed in so this strip and the map can never fold by
   *  two different numbers. `orgTimezone` below is for DISPLAY only. */
  offsetSec: number;
  zoneMap: Map<string, RelayZone>;
  routers: EspNowRouter[];
  orgTimezone: string;
}) {
  // Show ±3 steps around current time.
  //
  // Ordered and compared in the LOCAL day, displayed from the raw UTC value.
  // Sorting on start_sec directly put a 9 AM local run (23:00 UTC on a +10
  // site) after every afternoon program, and "Today's Schedule" then listed the
  // day out of order with the wrong step highlighted. See localDaySec.
  const sorted = [...timeline]
    .map(s => ({
      step: s,
      start: localDaySec(s.start_sec, offsetSec),
      end: localDaySec(s.start_sec, offsetSec) + (s.end_sec - s.start_sec),
    }))
    .sort((a, b) => a.start - b.start);
  const activeIdx = sorted.findIndex(s => !s.step.skipped && nowSec >= s.start && nowSec < s.end);
  const focusIdx = activeIdx >= 0 ? activeIdx : sorted.findIndex(s => s.start > nowSec);
  const start = Math.max(0, focusIdx - 2);
  const visible = sorted.slice(start, start + 7);

  function routerName(mac: string): string {
    return routers.find(r => r.router_mac === mac)?.name || mac.slice(-6);
  }

  return (
    <div className="border border-gray-200 rounded-xl bg-white p-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Today's Schedule</p>
      <div className="space-y-1">
        {visible.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No schedule for today.</p>
        ) : (
          visible.map(({ step, start, end }) => {
            // Highlighting and the countdowns compare in the local frame; the
            // clock times below are still rendered from the raw UTC values,
            // which is what utcSecToLocalHHMM expects. Subtracting the folded
            // nowSec from a raw start_sec/end_sec is off by the site's offset
            // and read "-35000s left" on every running step.
            // A skipped row is never the active one: nothing opened. The
            // sequence after it carries the real times, re-timed on the server.
            const skipped  = !!step.skipped;
            const isActive = !skipped && nowSec >= start && nowSec < end;
            const isPast   = skipped || nowSec >= end;

            return (
              <div
                key={`${step.start_id ?? ''}-${step.step_id}`}
                className={`flex items-start gap-3 px-3 py-2 rounded-lg ${
                  skipped ? 'bg-red-50 border border-red-200' : isActive ? 'bg-green-50 border border-green-200' : isPast ? 'bg-gray-50 opacity-60' : 'bg-blue-50 border border-blue-100'
                }`}
              >
                {/* Time — start_sec is UTC, convert to org local time for display */}
                <div className="text-xs text-gray-500 font-mono flex-shrink-0 pt-0.5">
                  {utcSecToLocalHHMM(step.start_sec, orgTimezone)}
                  {!skipped && (
                    <>
                      <span className="text-gray-300 mx-0.5">–</span>
                      {utcSecToLocalHHMM(step.end_sec, orgTimezone)}
                    </>
                  )}
                </div>

                {/* Status dot */}
                <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                  skipped ? 'bg-red-500' : isActive ? 'bg-green-500 ring-2 ring-green-200' : isPast ? 'bg-gray-300' : 'bg-blue-400'
                }`} />

                {/* Relays */}
                <div className="flex-1 flex flex-wrap gap-1">
                  {step.relays.map((r, idx) => {
                    const zone = zoneMap.get(`${r.router_mac}:${r.relay_index}`);
                    const label = relayDisplayName(
                      zone?.zone_name,
                      routers.find(rt => rt.router_mac === r.router_mac)?.relay_labels,
                      r.relay_index
                    );
                    return (
                      <span
                        key={idx}
                        className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                        style={{ background: zone?.zone_color ?? '#3B82F6' }}
                        title={routerName(r.router_mac)}
                      >
                        {label}
                        {/* Each relay in a step runs for its own length, so the
                            step's times do not answer "how long is this valve
                            on for". Hiding that in a tooltip meant the answer
                            existed only on a device with a mouse. */}
                        <span className="opacity-75"> · {formatDuration(r.duration_sec)}</span>
                      </span>
                    );
                  })}
                </div>

                {/* Timer */}
                {skipped && (
                  <div
                    className="text-xs text-red-700 font-medium flex-shrink-0"
                    title={(step.silent_nodes ?? []).map(routerName).join(', ')}
                  >
                    Skipped
                  </div>
                )}
                {isActive && (
                  <div className="text-xs text-green-700 font-medium flex-shrink-0 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(end - nowSec)} left
                  </div>
                )}
                {!isActive && !isPast && (
                  <div className="text-xs text-blue-600 flex-shrink-0">
                    in {formatDuration(start - nowSec)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Sensor Device Marker ────────────────────────────────────────────────────

function SensorDeviceMarker({
  device,
  onDragEnd,
}: {
  device: SensorDevice & { channels?: SensorChannel[]; latestReadings?: Array<{ channel_index: number; channel_kind: string; value: number | null; reading_time: string }> };
  onDragEnd: (lat: number, lng: number) => void;
}) {
  const icon = createSensorIcon(device.online);
  const channels = device.channels ?? [];
  const readings = device.latestReadings ?? [];

  function getReading(kind: string, index: number) {
    return readings.find(r => r.channel_index === index && r.channel_kind === kind);
  }

  return (
    <Marker
      position={[device.lat!, device.lng!]}
      icon={icon}
      draggable
      eventHandlers={{
        dragend: e => {
          const ll = (e.target as L.Marker).getLatLng();
          onDragEnd(ll.lat, ll.lng);
        },
      }}
    >
      <Popup>
        <div className="text-sm min-w-[180px]">
          <div className="font-semibold mb-1">{device.name || device.device_id}</div>
          <div className="flex items-center gap-1 mb-2 text-xs text-gray-500">
            <span className={`w-2 h-2 rounded-full ${device.online ? 'bg-violet-500' : 'bg-gray-400'}`} />
            {device.online ? 'Online' : 'Offline'}
          </div>
          {channels.length > 0 ? (
            <div className="space-y-1">
              {channels.map(ch => {
                const r = getReading(ch.channel_kind, ch.channel_index);
                return (
                  <div key={`${ch.channel_kind}-${ch.channel_index}`} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">{ch.name || `Ch ${ch.channel_index}`}</span>
                    <span className="font-semibold text-gray-800">
                      {r?.value != null ? `${Number(r.value).toFixed(1)}${ch.type_unit ? ' ' + ch.type_unit : ''}` : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-gray-400 italic">No channels configured</div>
          )}
          <div className="text-xs text-gray-400 mt-1">Drag to reposition</div>
        </div>
      </Popup>
    </Marker>
  );
}
